import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  EyeOff,
  FileSearch,
  Landmark,
  Lock,
  Shield,
  Sparkles,
  Truck,
  type LucideIcon,
} from "lucide-react";
import {
  useComplianceAuth,
  type ComplianceRole,
} from "../context/ComplianceAuthContext";
import { setComplianceContext } from "../services/compliance-api";
import LeadCaptureModal from "../components/LeadCaptureModal";
import type { LeadRequestType } from "../services/lead-capture-api";

const ROLES: { value: ComplianceRole; label: string; description: string }[] = [
  {
    value: "COMPLIANCE_ADMIN",
    label: "Compliance Admin",
    description: "Full access - manage all workflows, audit, and settings",
  },
  {
    value: "FINANCE_REVIEWER",
    label: "Finance Reviewer",
    description: "VAT and invoice exception review",
  },
  {
    value: "SHIPMENT_OPERATOR",
    label: "Shipment Reviewer",
    description: "eCMR and shipment exception review",
  },
  {
    value: "AUDITOR",
    label: "Auditor",
    description: "Audit history, export, and evidence review",
  },
  {
    value: "ROOT_ADMIN",
    label: "Root Admin",
    description: "LightLink staff only — Workflow Manager, Users, and Integrations across all tenants. Tenant ID must be tenant_admin.",
  },
];

const DOMAINS: {
  name: string;
  status: "Live";
  description: string;
  Icon: LucideIcon;
  accent: string;
}[] = [
  {
    name: "Logistics",
    status: "Live",
    description: "Shipment evidence, VAT checks, eCMR review, and audit trails.",
    Icon: Truck,
    accent: "#38bdf8",
  },
];

const LANE_CAPABILITIES: {
  title: string;
  description: string;
  Icon: LucideIcon;
}[] = [
  {
    title: "Document Intelligence",
    description: "Batch intake, classification, and evidence completeness.",
    Icon: FileSearch,
  },
  {
    title: "Registry Checks",
    description: "VIES VAT, EORI, TARIC, and sanctions validation flows.",
    Icon: Landmark,
  },
  {
    title: "Case Workflow",
    description: "Review queues, role handoff, exceptions, and approvals.",
    Icon: CheckCircle2,
  },
  {
    title: "Audit Trail",
    description: "Tenant-aware records, exports, and compliance history.",
    Icon: ClipboardCheck,
  },
];

export default function ComplianceLoginPage() {
  const { login } = useComplianceAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from =
    (location.state as { from?: string } | null)?.from ??
    "/Compliance/logistics/dashboard";

  const [tenantId, setTenantId] = useState("");
  const [token, setToken] = useState("");
  const [role, setRole] = useState<ComplianceRole>("COMPLIANCE_ADMIN");
  const [mfaVerified, setMfaVerified] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalRequestType, setModalRequestType] =
    useState<LeadRequestType | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!tenantId.trim()) {
      setError("Tenant ID is required.");
      return;
    }
    if (!token.trim()) {
      setError("License key is required.");
      return;
    }

    setLoading(true);
    try {
      // Verify the managed license key before creating the local session.
      const res = await fetch("/compliance-api/api/compliance/auth/license/verify", {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenantId,
          "X-Compliance-Role": role,
          ...(mfaVerified ? { "X-Compliance-MFA": "verified" } : {}),
        },
      });

      if (!res.ok) {
        setError(
          res.status === 401 || res.status === 403
            ? "Invalid license key, expired license, or insufficient role permissions."
            : `Compliance service returned ${res.status}. Check that the backend is running.`,
        );
        return;
      }

      login({ token, tenantId, role, mfaVerified });
      setComplianceContext(tenantId, role);
      navigate(from, { replace: true });
    } catch {
      setError(
        "Could not reach the compliance service. Verify the backend is running.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen overflow-hidden bg-[#050814] text-white">
      <LeadCaptureModal
        open={modalRequestType !== null}
        onClose={() => setModalRequestType(null)}
        requestType={modalRequestType ?? "demo"}
        title={
          modalRequestType === "pilot" || modalRequestType === "pilot_review"
            ? "Explore the 30-Day Pilot"
            : "Book a Compliance Readiness Demo"
        }
        description="Tell us a bit about your team and we'll get back to you shortly."
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(56,189,248,0.18),transparent_28%),radial-gradient(circle_at_82%_0%,rgba(167,139,250,0.14),transparent_24%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(2,6,23,1))]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/50 to-transparent" />

      <section className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-8 px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid flex-1 items-center gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-200">
              <Sparkles size={14} />
              Magnetar AI Solutions Product
            </div>

            <div className="space-y-5">
              <h1 className="max-w-3xl text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
                LightLink AI
              </h1>
              <p className="max-w-2xl text-xl font-semibold text-slate-200 sm:text-2xl">
                AI compliance control tower for UK and European logistics operations.
              </p>
              <p className="max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
                LightLink AI helps logistics and manufacturing teams detect
                shipment document gaps, VAT and registry risks, workflow
                exceptions, and audit exposure before they become customs
                delays, finance issues, or customer disputes.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setModalRequestType("demo")}
                  className="inline-flex items-center justify-center rounded-lg bg-sky-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-950/30 transition-colors hover:bg-sky-400"
                >
                  Book a Compliance Readiness Demo
                </button>
                <button
                  type="button"
                  onClick={() => setModalRequestType("pilot")}
                  className="inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors hover:border-sky-300/50 hover:bg-sky-300/10"
                >
                  Explore the 30-Day Pilot
                </button>
              </div>
              <p className="max-w-xl text-xs leading-5 text-slate-500">
                Built for teams that need document intelligence, registry
                checks, case workflows, and audit-ready shipment evidence.
              </p>
            </div>

            <section className="max-w-2xl rounded-xl border border-white/10 bg-white/[0.05] p-5 text-slate-300 shadow-xl shadow-slate-950/20">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-white">
                  About LightLink AI
                </h2>
                <span className="rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                  Current product capabilities
                </span>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-300 sm:text-base">
                LightLink AI is the active logistics compliance workspace for regulated EU shipment operations, combining document intelligence, registry verification, case review, workflow control, risk oversight, and audit-ready reporting.
              </p>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-400">
                <li className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-sky-300" size={16} />
                  <span>
                    Upload up to 24 shipment documents, auto-generate a unique batch ID, classify evidence, and analyze batches without manual IDs.
                  </span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-sky-300" size={16} />
                  <span>
                    Run V2 Intelligence checks for VIES VAT validation, registry safety, sanctions screening, shipment consistency, and financial exposure.
                  </span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-sky-300" size={16} />
                  <span>
                    Manage shipment cases, workflows, risk reviews, document history, audit trails, and structured PDF compliance reports.
                  </span>
                </li>
              </ul>
            </section>

            <div className="grid gap-3 sm:grid-cols-3">
              {[
                "EU compliance workflows",
                "Tenant-aware operations",
                "Audit-ready evidence",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-300"
                >
                  <CheckCircle2 className="mb-2 text-sky-300" size={18} />
                  {item}
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm leading-6 text-emerald-100">
              The live compliance portal is focused on logistics operations.
            </div>

            <section className="rounded-xl border border-white/10 bg-white/[0.045] p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-200">
                    Manufacturing logistics readiness
                  </p>
                  <h2 className="mt-2 text-lg font-semibold text-white">
                    Built for manufacturers with internal logistics operations.
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
                    Review shipment evidence, VAT exposure, supplier transport
                    records, and audit history before issues reach finance,
                    customs, or customers.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setModalRequestType("pilot_review")}
                  className="inline-flex shrink-0 items-center justify-center rounded-lg border border-emerald-300/30 bg-emerald-300/10 px-4 py-2.5 text-sm font-semibold text-emerald-100 transition-colors hover:bg-emerald-300/15"
                >
                  Request a Pilot Review
                </button>
              </div>
            </section>
          </div>

          <div className="space-y-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-200">
                  Compliance Lane
                </p>
                <h2 className="mt-2 text-2xl font-bold text-white">
                  Logistics compliance lane
                </h2>
              </div>
              <div className="hidden rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-semibold text-emerald-200 sm:block">
                Logistics active
              </div>
            </div>

            <div className="grid items-stretch gap-4 lg:grid-cols-[0.95fr_1.05fr]">
              <section className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.055] p-4 shadow-2xl shadow-slate-950/25 backdrop-blur sm:p-5">
                {DOMAINS.map(({ name, status, description, Icon }) => (
                  <div
                    key={name}
                    className="rounded-xl border border-sky-300/40 bg-sky-400/10 p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-sky-300/30 bg-slate-950/70 text-sky-300">
                        <Icon size={22} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold text-white">
                            {name}
                          </h3>
                          <span className="rounded-full bg-emerald-300/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-200">
                            {status}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slate-300">
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="mt-4 grid gap-3">
                  {LANE_CAPABILITIES.map(({ title, description, Icon }) => (
                    <div
                      key={title}
                      className="flex gap-3 rounded-xl border border-white/10 bg-slate-950/45 p-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-sky-200">
                        <Icon size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">
                          {title}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-400">
                          {description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid gap-3 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-200">
                      Production lane
                    </p>
                    <p className="mt-2 text-sm text-emerald-50">
                      EU logistics compliance portal
                    </p>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2">
                    <Activity className="text-emerald-200" size={18} />
                    <div>
                      <p className="text-xs text-emerald-100">System status</p>
                      <p className="text-sm font-semibold text-white">
                        Active and monitored
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-sky-300/30 bg-slate-950/80 p-4 shadow-2xl shadow-sky-950/40 backdrop-blur sm:p-5">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-sky-300/30 bg-sky-400/15">
                    <Shield className="text-sky-300" size={25} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Logistics Compliance Portal
                    </h2>
                    <p className="text-sm text-slate-400">
                      Separate tenant credentials required
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-slate-900/90 p-5 sm:p-6">
                  {error && (
                    <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-700/50 bg-red-900/30 px-4 py-3 text-sm text-red-300">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <form
                    onSubmit={(e) => void handleSubmit(e)}
                    className="space-y-5"
                  >
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-300">
                        Tenant ID
                      </label>
                      <input
                        type="text"
                        required
                        autoComplete="off"
                        placeholder="e.g. tenant_nl_001"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-300">
                        License Key
                      </label>
                      <div className="relative">
                        <input
                          type={showToken ? "text" : "password"}
                          required
                          autoComplete="current-password"
                          placeholder="Unique license key issued by your compliance admin"
                          value={token}
                          onChange={(e) => setToken(e.target.value)}
                          className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2.5 pr-11 text-sm text-white placeholder-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowToken((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                          tabIndex={-1}
                          aria-label={
                            showToken
                              ? "Hide license key"
                              : "Show license key"
                          }
                        >
                          {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-300">
                        Compliance Role
                      </label>
                      <div className="space-y-2">
                        {ROLES.map((r) => (
                          <label
                            key={r.value}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                              role === r.value
                                ? "border-sky-500 bg-sky-600/10"
                                : "border-slate-700 hover:border-slate-600"
                            }`}
                          >
                            <input
                              type="radio"
                              name="role"
                              value={r.value}
                              checked={role === r.value}
                              onChange={() => setRole(r.value)}
                              className="mt-0.5 accent-sky-500"
                            />
                            <div>
                              <p className="text-sm font-medium text-white">
                                {r.label}
                              </p>
                              <p className="mt-0.5 text-xs text-slate-400">
                                {r.description}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <label className="flex cursor-pointer select-none items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mfaVerified}
                        onChange={(e) => setMfaVerified(e.target.checked)}
                        className="h-4 w-4 rounded accent-sky-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-300">
                          MFA verified this session
                        </p>
                        <p className="text-xs text-slate-500">
                          Required for document upload and sensitive operations
                        </p>
                      </div>
                    </label>

                    <button
                      type="submit"
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Lock size={16} />
                      {loading ? "Verifying..." : "Access Compliance Portal"}
                    </button>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </div>

        <footer className="border-t border-white/10 pt-4 text-center text-xs leading-6 text-slate-400">
          <p>
            LightLink AI is a product of Magnetar AI Solutions.
          </p>
          <p>
            Contact{" "}
            <a
              href="mailto:support@magentarai.co.in"
              className="font-medium text-sky-300 hover:text-sky-200"
            >
              support@magentarai.co.in
            </a>{" "}
            ·{" "}
            <a
              href="https://www.magnetarai.co.in"
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-sky-300 hover:text-sky-200"
            >
              Magnetar AI Solutions
            </a>
          </p>
        </footer>
      </section>
    </main>
  );
}
